import React, { useState, useMemo } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import DialogContent from '@material-ui/core/DialogContent';
import StandardFab from '../../../../../customComponents/button/StandardFab';
import { DialogActions, Divider, FormControl, TextField, Typography } from '@material-ui/core';
import OutlinedStadiumButton from '../../../../../customComponents/button/OutlinedStadiumButton';
import StadiumButton from '../../../../../customComponents/button/StadiumButton';
import { ITransformer } from '../../../../../../@types/model/transformer/transformer';
import TransformerDropdown from '../../../../../customComponents/selector/transformer/TransformerSelector';
import TransformerSPUMeterProblemSelector from '../../../../../customComponents/selector/transformer/SPUMeterProblemSelector';
import SPUMeterReadingInput, { ISPUMeterReading } from '../../../../../customComponents/input/transformer/SPUMeterReadingInput';
import MultiFileUploadProgress from '../../../../../customComponents/file/MultiFileUploadProgress';
import TransformerFunctions from '../../../../../../store/transformer/functions';
import uuid from 'uuid';
import { EmployeeHelper, IUserSession } from '../../../../../../@types/employee';
import { firestore } from 'firebase/app';
import TransformerReadingHelper, { IMeterReadingsView, ITransformerReading } from '../../../../../../@types/model/transformer/reading/transformerReading';
import moment from 'moment';
import generalFunctions from '../../../../../../store/general/functions';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { DATE_FORMAT_DEFAULT_NO_TIME } from '../../../../../../appConstants';
import ProjectDialog from '../../../../../customComponentV2/dialog/ProjectDialog';

interface ISPUMeterCreateDialogProps {
    session ?: IUserSession | null;
    readings ?: Array<IMeterReadingsView>;
}

const SPUMeterCreateDialog = (props : ISPUMeterCreateDialogProps) : React.ReactElement => {
    const [open, setOpen] = useState<boolean>(false);
    const [transformer, setTransformer] = useState<ITransformer | null>(null);
    const [date, setDate] = useState<moment.Moment>(moment.utc().startOf('day'));
    const [guid, setGuid] = useState<string>('');
    const [readings, setReadings] = useState<Array<ISPUMeterReading>>([]);
    const [conditions, setConditions] = useState<Array<string>>([]);
    const [other, setOther] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const onClick = (): void => {
        setOpen(true);
        setDate(moment.utc().startOf('day'));
        setTransformer(null);
        setGuid(uuid.v4());
        setReadings([]);
        setConditions([]);
        setIsUploading(false);
    };

    const onClose = (): void => {
        setOpen(false);
        setIsUploading(false);
        setIsLoading(false);
    };

    const onSubmit = (event : React.FormEvent): void => {
        event.preventDefault();
        save();
    };

    const save = async (): Promise<void> => {
        if (!props.session || !transformer) return;
        if (readings.some(n => !Number(n.meterReading))) return;

        if (other && !conditions.includes(other)) {
            conditions.push(other);
        }

        const reading : ITransformerReading = {
            ref: TransformerReadingHelper.newDoc(),
            GUID: guid,
            Conditions: conditions,
            Electricalpoint: transformer.ref,
            Geom: new firestore.GeoPoint(0, 0),
            isWeb: true,
            TransformerMeterNumberReadings: readings.map(n => ({
                ImageFileName: n.image ? n.image.name : null,
                ImageFileUrl: null,
                ItemNumber: n.itemNumber,
                MeterNumber: n.meterNumber,
                NumberOfDigits: n.numberOfDigits,
                Reading: Number(n.meterReading),
                RecordingFileName: '',
                Usage: n.usage,
                Division: transformer.Division,
                Group: transformer.EPGroup ?? '',
                Rollover: n.rollover,
            })),
            Date: firestore.Timestamp.fromMillis(date.valueOf()),
            Division: transformer.Division,
            Group: transformer.EPGroup ?? '',
            SubArea: transformer.SubArea,
            CreatedBy: EmployeeHelper.doc(props.session.firebaseUser.uid),
            CreatedByName: props.session.firebaseUser.displayName ?? '',
            CreatedByEmployee: props.session.employee.EmployeeNumber ?? '',
            CreatedOn: firestore.Timestamp.now(),
            UpdatedBy: EmployeeHelper.doc(props.session.firebaseUser.uid),
            UpdatedByName: props.session.firebaseUser.displayName ?? '',
            UpdatedByEmployee: props.session.employee.EmployeeNumber ?? '',
            UpdatedOn: firestore.Timestamp.now(),
        };

        try {
            setIsLoading(true);
            await TransformerReadingHelper.create(reading);
            setIsUploading(true);
        } catch (ex) {
            if (ex instanceof Error) generalFunctions.generalShowErrorSnackbar(ex.message ? ex.message : 'Error Creating Reading');
            setIsLoading(false);
        }
    };

    const getExisting = useMemo<Array<IMeterReadingsView>>(() => {
        if (!props.readings) return [];
        if (!transformer?.EPNumber) return [];

        return props.readings
            .filter(x => x.EPNumber === transformer?.EPNumber)
            .filter(x => moment.utc(x.Date).format('MM-YYYY') === date.format('MM-YYYY'));
    }, [props.readings, transformer?.EPNumber, date]);

    const onEPNumberChange = (value ?: ITransformer): void => {
        setTransformer(value ?? null);
        setReadings(value?.TransformerMeterNumbers?.map((meterNumber) => ({
            image: null,
            itemNumber: meterNumber.ItemNumber,
            meterNumber: meterNumber.Number,
            numberOfDigits: meterNumber.NumberOfDigits,
            meterReading: '',
            usage: 0,
            rollover: false,
            errors: [],
            exists: getExisting.some(x => x.MeterNumber === meterNumber.Number),
        })) ?? []);
    };

    const onConditionChange = (values : Array<string>): void => {
        setConditions(values.slice());
    };

    const onMeterReadingChange = (reading : ISPUMeterReading): void => {
        const updatedReadings = readings.slice();
        const index = updatedReadings.findIndex(x => x.itemNumber === reading.itemNumber);
        updatedReadings.splice(index, 1, { ...reading });
        setReadings(updatedReadings);
    };

    const onFileUpload = (file : File): firebase.storage.UploadTask | null => {
        if (!transformer) return null;
        const reading = readings.find(x => x.image?.name === file.name);
        if (!reading) return null;
        return TransformerFunctions.uploadReadingFile(transformer.ref.id, file, guid, reading.itemNumber);
    };

    const onDateChange = (newDate : moment.Moment | null): void => {
        if (!newDate) return;
        setDate(newDate.startOf('day').utc());
    };

    const onOtherChange = (event : React.ChangeEvent<HTMLInputElement>): void => {
        setOther(event.target.value);
    };

    const onDone = (): void => {
        onClose();
    };

    const files = useMemo<Array<File>>(() => {
        const result : Array<File> = [];
        readings.forEach(n => {
            if (n.image) result.push(n.image);
        });
        return result;
    }, [readings]);

    return (
        <>
            <Tooltip title='Create'>
                <div>
                    <StandardFab color='primary' onClick={onClick} aria-label='tooltip'>
                        <Icon>add</Icon>
                    </StandardFab>
                </div>
            </Tooltip>
            <ProjectDialog
                title='Create SPU Reading'
                isOpen={open}
                isLoadingCircular={isLoading}
                onClose={onClose}
                maxWidth={'md'}
                fullWidth
            >
                <form onSubmit={onSubmit}>
                    <DialogContent className='fdc flx1 hfill bcg0'>
                        {
                            !isUploading &&
                            <div className='fdr'>
                                <div className='fdc flx1 pr5 w240'>
                                    <TransformerDropdown
                                        value={transformer?.EPNumber}
                                        label='Select Transformer'
                                        type='spu'
                                        fullWidth
                                        onChange={onEPNumberChange}
                                        required
                                        activeOnly
                                    />
                                </div>
                                <div className='fdc flx1 pr5 w240'>
                                    <KeyboardDatePicker
                                        value={date}
                                        label='Reading Date'
                                        format={DATE_FORMAT_DEFAULT_NO_TIME}
                                        fullWidth
                                        onChange={onDateChange}
                                        required
                                    />
                                </div>
                            </div>
                        }
                        {
                            !isUploading &&
                            <div className='fdr ais mt15'>
                                <div className='fdc flx1 ais pr5'>
                                    <div className='fdc mb10'>
                                        <Typography className='fs16 lh37 cg3'>
                                            Readings
                                        </Typography>
                                    </div>
                                    <div className='fdc'>
                                        {
                                            readings.map((n) => (
                                                <SPUMeterReadingInput
                                                    readingGuid={guid}
                                                    key={n.itemNumber}
                                                    value={n}
                                                    onChange={onMeterReadingChange}
                                                    exists={getExisting.some(x => x.MeterNumber === n.meterNumber)}
                                                />
                                            ))
                                        }
                                    </div>
                                    <Divider />
                                </div>
                            </div>
                        }
                        {
                            !isUploading &&
                            <div className='fdr ais mt15'>
                                <div className='fdc flx1 ais pr5'>
                                    <div className='fdc mb10'>
                                        <Typography className='fs16 lh37 cg3'>
                                            Problems
                                        </Typography>
                                    </div>
                                    <div className='fdr ais mb10'>
                                        <TransformerSPUMeterProblemSelector values={conditions} onChange={onConditionChange} />
                                    </div>
                                    <div className={'fdr pb10'}>
                                        <FormControl className={'flx1'}>
                                            <TextField
                                                autoComplete='off'
                                                id='other'
                                                label='Other'
                                                value={other}
                                                margin='normal'
                                                className={'TextField'}
                                                onChange={onOtherChange}
                                            />
                                        </FormControl>
                                        <div className={'flx1'}/>
                                    </div>
                                    <Divider />
                                </div>
                            </div>
                        }
                        {
                            isUploading &&
                            <MultiFileUploadProgress files={files} onFileUpload={onFileUpload} onDone={onDone} />
                        }
                    </DialogContent>
                    <DialogActions className='bcg0'>
                        <OutlinedStadiumButton disabled={isLoading || isUploading} className='mr16' variant='text' type='reset' onClick={onClose}>
                            CANCEL
                        </OutlinedStadiumButton>
                        <StadiumButton variant='contained' disabled={isLoading || isUploading} type='submit'>
                            CONFIRM
                        </StadiumButton>
                    </DialogActions>
                </form>
            </ProjectDialog>
        </>
    );
};

export default SPUMeterCreateDialog;
